import { LINE_PAUSE_MS, type RealtimeFragment } from '@crowdex/provider-contracts/realtime-call';

/** A line of what was said in the call; `streaming` while its words still arrive. */
export type TranscriptLine = {
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  /** When its last word ended, from the call's start. */
  endMs?: number;
};

/** How many lines the call shows. */
const SHOWN_LINES = 6;

/**
 * The words of a fragment, as they are said: on the speaker's line being said (the other may
 * speak over it meanwhile), else on a new line. A line is said once a pause follows it.
 */
export function addWords(
  lines: readonly TranscriptLine[],
  fragment: RealtimeFragment,
): TranscriptLine[] {
  const index = lines.findLastIndex(
    (line) => line.streaming === true && line.role === fragment.role,
  );
  const next =
    index !== -1 && fragment.startMs - (lines[index]?.endMs ?? 0) < LINE_PAUSE_MS
      ? lines.map((line, at) =>
          at === index
            ? {
                ...line,
                text: line.text + fragment.text,
                endMs: Math.max(line.endMs ?? 0, fragment.endMs),
              }
            : line,
        )
      : [
          ...lines,
          { role: fragment.role, text: fragment.text, streaming: true, endMs: fragment.endMs },
        ];
  const latest = Math.max(fragment.endMs, ...next.map((line) => line.endMs ?? 0));
  return next.slice(-SHOWN_LINES).map((line) => {
    if (line.streaming !== true || latest - (line.endMs ?? 0) < LINE_PAUSE_MS) return line;
    const { streaming: _streaming, ...said } = line;
    return said;
  });
}
